import type { AnalyticsProvider } from '../types';

/**
 * Fan an `AnalyticsProvider` call out to several concretes (ADR-053). Used on
 * web + mobile to run PostHog product-analytics AND Datadog RUM off the single
 * `analytics` seam: `setUser` reaches both, so the same server-minted subject
 * lands on RUM `usr.id` and PostHog `distinct_id` (ADR-069). When only one concrete is passed it behaves
 * as that concrete.
 *
 * Best-effort per member (ADR-068 Rule 6): one sink throwing must not stop the
 * others or take down the app, so each fan-out call is isolated.
 */
export function compositeAnalytics(providers: AnalyticsProvider[]): AnalyticsProvider {
  const each = (fn: (a: AnalyticsProvider) => void) => {
    for (const a of providers) {
      try {
        fn(a);
      } catch {
        // best-effort: a failing analytics sink never breaks its siblings or the app
      }
    }
  };

  return {
    track: (event, properties, options) =>
      each((a) =>
        options === undefined ? a.track(event, properties) : a.track(event, properties, options),
      ),
    setUser: (userId, properties) =>
      each((a) => (properties === undefined ? a.setUser(userId) : a.setUser(userId, properties))),
    clearUser: () => each((a) => a.clearUser()),
    flush: async () => {
      await Promise.all(
        providers.map(async (a) => {
          try {
            await a.flush?.();
          } catch {
            // best-effort drain
          }
        }),
      );
    },
  };
}
